import { useEffect, useState } from 'react'

// Vite turns this into a map: { '../help/en/quick-start.md': () => Promise<string>, ... }
// The `?raw` query + import:'default' makes each loader resolve to the file's raw text.
// We match exactly one language directory + one slug so the top-level README.md
// doesn't get bundled as help content.
const helpFiles = import.meta.glob('../help/*/*.md', {
  query: '?raw',
  import: 'default',
})

/**
 * Build the lookup key used by Vite's glob map for a given language + section.
 * Kept as a pure helper so the parity test can re-use the same logic.
 */
export function buildGlobKey(language, section) {
  return `../help/${language}/${section}.md`
}

/**
 * Resolve the markdown content for a given section in a given language.
 *
 * Strategy:
 *   1. Try `{language}/{section}.md` — on hit, return content with usedFallback=false.
 *   2. If missing and language !== 'en', try `en/{section}.md` — on hit, return with usedFallback=true.
 *   3. If both miss, return notFound=true.
 */
export function useHelpContent(section, language) {
  const [state, setState] = useState({
    loading: true,
    content: null,
    usedFallback: false,
    notFound: false,
  })

  useEffect(() => {
    let cancelled = false

    async function resolve() {
      setState({ loading: true, content: null, usedFallback: false, notFound: false })

      if (!section) {
        if (!cancelled) {
          setState({ loading: false, content: null, usedFallback: false, notFound: true })
        }
        return
      }

      const primaryKey = buildGlobKey(language, section)
      const fallbackKey = buildGlobKey('en', section)

      try {
        if (helpFiles[primaryKey]) {
          const content = await helpFiles[primaryKey]()
          if (!cancelled) {
            setState({ loading: false, content, usedFallback: false, notFound: false })
          }
          return
        }

        if (language !== 'en' && helpFiles[fallbackKey]) {
          const content = await helpFiles[fallbackKey]()
          if (!cancelled) {
            setState({ loading: false, content, usedFallback: true, notFound: false })
          }
          return
        }

        if (!cancelled) {
          setState({ loading: false, content: null, usedFallback: false, notFound: true })
        }
      } catch (err) {
        // Any loader error falls through to a notFound state — the page then
        // shows the generic "section not found" block instead of crashing.
         
        console.error('[useHelpContent] failed to load markdown', err)
        if (!cancelled) {
          setState({ loading: false, content: null, usedFallback: false, notFound: true })
        }
      }
    }

    resolve()

    return () => {
      cancelled = true
    }
  }, [section, language])

  return state
}

// Exposed for tests that need to enumerate which markdown files exist
// (used by the parity test).
export const __helpFilesForTests = helpFiles
